import { useState } from 'react';
import {
  AcceptLoiDialog,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DecisionConfirmDialog,
  HelpTip,
  LoiReviewTable,
  SellerVotePanel,
  responseCopy,
} from '@dataroom/shared';
import type {
  ExclusivityTerms,
  LoiDecision,
  LoiReviewItem,
  SellerVoteChoice,
} from '@dataroom/shared';

interface LoiManagementCardProps {
  lois: LoiReviewItem[];
  loading: boolean;
  error: string | null;
  /** Message from a failed response (e.g. 409 exclusivity / already decided). */
  actionError: string | null;
  /** Whether this owner may START a response (the room's "let the owners respond"
   * toggle, or a room the buyer or the seller runs). When false the LOIs are shown
   * without response buttons and a note says the intermediary enters them; the owner
   * still votes on whatever is proposed. */
  canRespond: boolean;
  /** Whether the room has loaded — while unknown neither actions nor the
   * note are shown (avoids a flash of the wrong state). */
  toggleReady: boolean;
  /** LOI id whose response is being recorded (disables its buttons). */
  decidingId: string | null;
  /** Record a response; an accept carries the exclusivity terms from the dialog. */
  onDecide: (loiId: string, decision: LoiDecision, exclusivity?: ExclusivityTerms) => void;
  onOpen: (loi: LoiReviewItem) => void;
  onDismissError: () => void;
  /** The room's IANA timezone — the accept dialog's "today". */
  timezone: string;
  /** Cast the viewer's Approve / Decline on a counter or decline proposed by another
   * owner or by the intermediary (an open `loi_response` vote on the LOI). */
  onVote?: (loiId: string, vote: SellerVoteChoice) => void;
  /** LOI id whose vote is being recorded (disables its panel's buttons). */
  votingId?: string | null;
  /** Plain server message from the last failed vote, with the LOI it was for. */
  voteError?: { loiId: string; message: string } | null;
}

/** A counter or decline that was proposed and the owners still vote on.
 * (An accepted LOI or an extension waiting for the vote is shown once, by the
 * page's exclusivity panel, so it is left out here.) */
const hasOpenResponseVote = (loi: LoiReviewItem) => loi.sellerVote?.kind === 'loi_response';

/**
 * The SELLER portal's LOI surface — the owners' console (ADR-0029). Lists every
 * LOI a buyer uploaded to the room (buyer, date, status, the signed document opened
 * read-only), and — when the room lets the owners respond — records Accept /
 * Counter / Reject, each behind an "Are you sure?". Whatever the intermediary
 * proposes shows below for the owners' Approve / Decline vote (each also behind an
 * "Are you sure?"). No funnel advance here: that stays admin-only. ACCEPT opens the
 * enforced-exclusivity dialog (ADR-0046): an owner's accept is their vote and takes
 * effect at once only when it carries the majority. Presentational — the page owns
 * the mutation.
 */
export function LoiManagementCard({
  lois,
  loading,
  error,
  actionError,
  canRespond,
  toggleReady,
  decidingId,
  onDecide,
  onOpen,
  onDismissError,
  timezone,
  onVote,
  votingId = null,
  voteError = null,
}: LoiManagementCardProps) {
  const hasSubmitted = lois.some((l) => l.status === 'submitted');
  // An LOI with an open vote offers no Accept / Counter / Reject again (the server
  // answers 409): it moves to the vote list below, where its panel is.
  const voting = lois.filter(hasOpenResponseVote);
  const others = lois.filter((l) => !hasOpenResponseVote(l));
  const [accepting, setAccepting] = useState<LoiReviewItem | null>(null);
  // The counter / decline waiting for its "Are you sure?".
  const [responding, setResponding] = useState<{
    loi: LoiReviewItem;
    decision: 'counter' | 'reject';
  } | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Letters of intent</CardTitle>
        <p className="text-sm text-muted-foreground">
          Signed letters of intent buyers have uploaded for this data room. Open each one, then
          record your response: accept, counter, or reject.
        </p>
        <p className="text-sm text-muted-foreground">
          Your response is your vote as an owner. If you own all of the company it is recorded at
          once. With several owners it is recorded as soon as owners holding more than half of the
          company approve, and the other owners are asked to vote. Whatever the intermediary
          proposes also waits for your vote.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {toggleReady && !canRespond && hasSubmitted ? (
          <div
            role="note"
            className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground"
          >
            The deal intermediary enters responses to letters of intent for you. Each one is only a
            proposal: it takes effect when owners holding more than 50% of the company approve it,
            and you vote on{' '}
            <span className="whitespace-nowrap">
              it here.
              <HelpTip id="seller.lois.intermediary-note" className="ml-1" />
            </span>
          </div>
        ) : null}

        {actionError ? (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            <span>{actionError}</span>
            <button
              type="button"
              onClick={onDismissError}
              className="text-xs font-medium underline"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : lois.length > 0 ? (
          <>
            {others.length > 0 ? (
              <>
                {/* The "?"s sit above the table, not in it: its wrapper scrolls sideways
                    and would clip their popovers. */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    What each status means
                    <HelpTip id="seller.lois.status" />
                  </span>
                  {toggleReady && canRespond && hasSubmitted ? (
                    <span className="inline-flex items-center gap-1.5">
                      Accept, Counter or Reject
                      <HelpTip id="seller.lois.response-actions" />
                    </span>
                  ) : null}
                </div>
                <LoiReviewTable
                  lois={others}
                  canRespond={toggleReady && canRespond}
                  decidingId={decidingId}
                  onDecide={(loiId, decision) => {
                    const loi = lois.find((l) => l.id === loiId) ?? null;
                    if (!loi) return;
                    if (decision === 'accept') setAccepting(loi);
                    else setResponding({ loi, decision });
                  }}
                  onOpen={onOpen}
                />
              </>
            ) : null}
            {voting.length > 0 ? (
              <section aria-label="Waiting for the owners' vote" className="space-y-3">
                <h3 className="text-sm font-semibold">Waiting for the owners&apos; vote</h3>
                {voting.map((loi, i) =>
                  loi.sellerVote ? (
                    <div key={loi.id} className="space-y-2">
                      <LoiReviewTable
                        lois={[loi]}
                        canRespond={false}
                        decidingId={null}
                        onDecide={onDecide}
                        onOpen={onOpen}
                      />
                      <SellerVotePanel
                        decision={loi.sellerVote}
                        titleAside={i === 0 ? <HelpTip id="seller.lois.vote-panel" /> : undefined}
                        pending={votingId === loi.id}
                        error={voteError?.loiId === loi.id ? voteError.message : null}
                        onVote={onVote ? (vote) => onVote(loi.id, vote) : undefined}
                      />
                    </div>
                  ) : null,
                )}
              </section>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No letters of intent yet. They appear here once a buyer uploads one.
          </p>
        )}
        {responding ? (
          <DecisionConfirmDialog
            copy={responseCopy(
              responding.decision,
              responding.loi.actorEmail ?? 'this buyer',
              'owner',
            )}
            danger={responding.decision === 'reject'}
            pending={decidingId === responding.loi.id}
            onCancel={() => setResponding(null)}
            onConfirm={() => {
              onDecide(responding.loi.id, responding.decision);
              setResponding(null);
            }}
          />
        ) : null}
        {accepting ? (
          <AcceptLoiDialog
            buyerLabel={accepting.actorEmail ?? 'this buyer'}
            teamSize={accepting.teamSize ?? 0}
            timezone={timezone}
            sellerValidationRequired={false}
            viewer="seller"
            onClose={() => setAccepting(null)}
            onConfirm={(terms) => {
              onDecide(accepting.id, 'accept', terms);
              setAccepting(null);
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
